import { Trans, useLingui } from '@lingui/react/macro';
import { Badge, cn } from '@utima/ui';
import { PauseCircle } from 'lucide-react';
import { memo } from 'react';

import { LockedFeatureTooltip } from './LockedFeatureTooltip';

export type AutoUpdatePausedBadgeProps = {
  paused: boolean;
  className?: string;
};

/**
 * Shown when a source (or the org default) carries a recurring auto-update
 * schedule but the plan no longer includes `KbAutoUpdates` (R4 gating). The
 * stored frequency is preserved, not reset — the sync crons simply skip
 * unentitled orgs — so we surface it as "paused" rather than pretending the
 * value is `Never`. Manual "Sync now" stays available.
 */
export const AutoUpdatePausedBadge = memo(function AutoUpdatePausedBadge({
  paused,
  className,
}: AutoUpdatePausedBadgeProps) {
  const { t } = useLingui();

  if (!paused) {
    return null;
  }

  return (
    <LockedFeatureTooltip
      locked
      message={t`Recurring auto-sync is paused because your plan no longer includes it. Your schedule is kept and resumes when you upgrade — you can still sync manually.`}
    >
      <Badge
        variant='warning'
        size='sm'
        outline
        className={cn('gap-1', className)}
      >
        <PauseCircle className='size-3' />
        <Trans>Auto-sync paused</Trans>
      </Badge>
    </LockedFeatureTooltip>
  );
});
